import {
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  HostListener,
  InjectionToken,
  NgZone,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { UntypedFormGroup } from '@angular/forms';
import { GoogleMapsService } from '@app/shared/maps/services/google/google-maps.service';
import { SharedModule } from '@app/shared/shared.module';
import { CampaignAddressSearchField, CampaignField } from '@fmnts/api/next';
import {
  FindResponse,
  LoqateAddressCaptureApi,
  RetrieveResponse,
} from '@fmnts/api/vendors/loqate';
import { ConnectionService } from '@fmnts/common';
import {
  Maybe,
  MaybeOption,
  Option,
  isString,
  optionHasValue,
} from '@fmnts/core';
import { includedIn, stringMatching, weighBy } from '@fmnts/core/collection';
import {
  AbstractDeclarativeInputFieldComponent,
  isFieldDeclaration,
  isFormSelectField,
} from '@fmnts/declarative-forms';
import { DeclarativeGeolocationService } from '@fmnts/declarative-forms/geolocation';
import { FormFieldService } from '@formunauts/shared/campaigning/data-access';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Boolean, String } from 'effect';
import {
  EMPTY,
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  map,
  merge,
  of,
  partition,
  pipe,
  repeat,
  skip,
  skipWhile,
  switchMap,
  take,
  takeUntil,
  tap,
  timeout,
} from 'rxjs';
import { SharedDeclarativeCampaignFormsModule } from '../../shared-declarative-next-forms.module';
import { excludeAddressComponentsWithType } from './address-search-field.util';

type FieldValue = string | null;

interface PrefillMapping {
  [fieldKey: string]: string[];
}

export enum AddressDataState {
  Init = 'init',
  Loading = 'loading',
  Loaded = 'loaded',
}

/**
 * Array with all the types returned by the google address search
 * for which the `short_name` property should be used,
 * instead of `long_name`
 */
const useShortNameComponentTypes = ['country'];

interface AddressSearchFieldState {
  searchTerm: string;
  hasFocus: boolean;
  hasResults: boolean;
  state: AddressDataState;
  results: FindResponse[];
}
const AddressSearchFieldStore = signalStore(
  withState<AddressSearchFieldState>({
    searchTerm: '',
    hasFocus: false,
    hasResults: false,
    state: AddressDataState.Init,
    results: [],
  }),
  withMethods((store) => ({
    setSearchTerm: rxMethod<string>(
      pipe(
        tap<string>((searchTerm) =>
          patchState(store, { searchTerm, hasResults: false }),
        ),
      ),
    ),
    setResults: rxMethod<FindResponse[]>(
      pipe(
        tap<FindResponse[]>((results) =>
          patchState(store, {
            results,
            state: AddressDataState.Loaded,
            hasResults: true,
          }),
        ),
      ),
    ),
    setState: rxMethod<AddressDataState>(
      pipe(tap<AddressDataState>((state) => patchState(store, { state }))),
    ),
    focus() {
      patchState(store, { hasFocus: true });
    },
    blur() {
      patchState(store, { hasFocus: false });
    },
  })),
);

interface AddressSearchConfig {
  debounce: number;
}
export const AddressSearchConfigToken = new InjectionToken<AddressSearchConfig>(
  '@formunauts/next/forms/address-search-field/config',
  {
    factory: () => ({
      debounce: 300,
    }),
  },
);

@Component({
  selector: 'next-address-search-field',
  templateUrl: './address-search-field.component.html',
  styleUrl: './address-search-field.component.scss',
  standalone: true,
  imports: [SharedDeclarativeCampaignFormsModule, SharedModule],
  providers: [AddressSearchFieldStore],
  encapsulation: ViewEncapsulation.None,
})
export class AddressSearchFieldComponent
  extends AbstractDeclarativeInputFieldComponent<
    CampaignAddressSearchField,
    FieldValue
  >
  implements OnInit
{
  private readonly ngZone = inject(NgZone);
  private readonly loqateService = inject(LoqateAddressCaptureApi);
  private readonly connectionService = inject(ConnectionService);
  private readonly formHelper = inject(FormFieldService);
  private readonly googleMapsService = inject(GoogleMapsService);
  private readonly geolocation = inject(DeclarativeGeolocationService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly vm = inject(AddressSearchFieldStore);
  protected readonly config = inject(AddressSearchConfigToken);

  @HostBinding('class') componentClass = 'form-field form-field__text';

  /**
   * Value for the `autocomplete` attribute of the input field.
   */
  readonly autocomplete = input(this._getAutocompleteBehavior());

  @ViewChild('element', { static: true })
  element!: ElementRef<HTMLInputElement>;

  @ViewChild('dropdown', { static: true })
  dropdown!: ElementRef<HTMLLabelElement>;

  /** `true` if users can improve search result accuracy. */
  protected readonly canImproveSearchAccuracy =
    this.geolocation.canImproveAccuracy;

  /** Observable that emits with the found results */
  public results$: Observable<FindResponse[]> = toObservable(this.vm.results);

  // Observable that will emit with an array of results
  // If nothing was entered, it will emit an empty array
  private readonly searchValue$: Observable<{ text: string }> = toObservable(
    this.vm.searchTerm,
  ).pipe(
    map(String.trim),
    debounceTime(this.config.debounce),
    map((text) => ({ text })),
  );

  private readonly hasFocus$ = toObservable(this.vm.hasFocus);

  /** Observable that emits `true`, if the dropdown should be visible */
  protected readonly dropdownShown$: Observable<boolean> = combineLatest([
    this.hasFocus$,
    this.searchValue$,
    this.results$,
    toObservable(this.vm.state),
  ]).pipe(
    map(
      ([hasFocus, searchTerm, results, state]) =>
        (hasFocus && searchTerm.text.length > 0 && results.length > 0) ||
        (hasFocus && state === AddressDataState.Loading),
    ),
  );

  /** Emits when the user selects a search result */
  protected readonly selectedSearchResult$ = new Subject<FindResponse | null>();

  private _value: FieldValue = '';

  ngOnInit() {
    switch (this.provider) {
      case 'google':
        this.initializeGoogleSearch();
        break;
      case 'loqate':
        this.initializeUKSearch();
        break;
    }
  }

  get value(): FieldValue {
    return this._value;
  }

  set value(value: FieldValue) {
    this._value = value;
    this.vm.setSearchTerm(value ?? '');
    this._onChange(value);
    this._onTouched();
  }

  protected openDropdown() {
    this.vm.focus();
  }

  private closeDropdown() {
    this.vm.blur();
  }

  protected _onFocus() {
    if (this.provider === 'loqate') {
      this.geolocation.updatePosition();
    }
  }

  @HostListener('document:click', ['$event'])
  public clickOutsideHandler(event: MouseEvent) {
    if (!this.dropdown.nativeElement.contains(event.target as Node)) {
      this.closeDropdown();
    }
  }

  writeValue(value: unknown) {
    const newValue = isString(value) ? value : null;
    this._value = newValue;

    // There are different reasons why the value changed, which may include
    // - reset of the form
    // - result written by a selected search result
    const newValueAsString = newValue ?? '';
    if (newValueAsString.toLowerCase() !== this.vm.searchTerm().toLowerCase()) {
      this.vm.setSearchTerm(newValueAsString);
    }
  }

  get provider() {
    return this.field.widgetOptions.provider;
  }

  get autocapitalize(): string | undefined {
    return this.field.widgetOptions.autocapitalize;
  }

  get showError(): boolean {
    return this.control.touched && this.control.invalid;
  }

  private get mapping() {
    return this.field.widgetOptions.mapping || {};
  }

  private initializeGoogleSearch() {
    this.googleMapsService.stable$.pipe(take(1)).subscribe((api) => {
      if (!api || google === undefined) {
        throw new Error('Google Maps API was not loaded correctly');
      }

      const { key: fieldKey } = this.field;
      const autocomplete = new google.maps.places.Autocomplete(
        this.element.nativeElement,
        {
          fields: ['formatted_address', 'address_components'],
          types: ['address'],
        },
      );

      autocomplete.addListener('place_changed', () => {
        this.ngZone.run(() => {
          // Selecting an entry via mouse click will result in focusing on elements
          // beneath the list entry.
          (document.activeElement as HTMLElement).blur();

          const { address_components: components } = autocomplete.getPlace();
          // In case that the place could not be retrieved, components will be
          // undefined (e.g. when offline).
          if (!components) {
            return;
          }

          // Filter out components with type "postal_code_prefix"
          const filteredComponents = components.filter(
            excludeAddressComponentsWithType('postal_code_prefix'),
          );

          // TODO: Re-use mapping from GoogleMaps service
          const result =
            this.mapGoogleComponentsToFieldValues(filteredComponents);
          if (Object.keys(result).length === 0) {
            return;
          }

          if (fieldKey in result) {
            // The Autocomplete widget sets the value for the input,
            // resulting in showing the full formatted address. See [FO-484]
            // Angular won't notice that change and the value would be out of sync.
            this.element.nativeElement.value = result[fieldKey] as string;
          }

          this._patchFormWithValues(result);

          // Show error message if country is not in availableCountries
          // TODO: move error-handling for unknown values to `select-field.component`
          const country = components
            .find((c) => c.types.includes('country'))
            ?.short_name.toLowerCase();
          if (country && !this.availableCountries().includes(country)) {
            const countryControl = this.control.parent?.get('country');
            countryControl?.patchValue(country);
            countryControl?.setErrors({
              countryUnsupported: true,
            });
          }
        });
      });
    });
  }

  /**
   * @param components The components from the google place result
   * @param mapping The mapping of field names to google place types
   *
   * @returns
   * Mapping of google address component values to field keys
   */
  private mapGoogleComponentsToFieldValues(
    components: google.maps.GeocoderAddressComponent[],
    mapping: PrefillMapping = this.mapping,
  ) {
    const getValue = (componentTypes: string[]): Option<string> => {
      const component = components.find((c) =>
        c.types.some(includedIn(componentTypes)),
      );

      // There is no component with the any of the given component types
      if (!component) {
        return null;
      }

      // Use `short_name` if the component_types include any of the special types
      return componentTypes.some(includedIn(useShortNameComponentTypes))
        ? component.short_name.toLowerCase()
        : component.long_name;
    };

    return this.mapResultToFormValue(mapping, getValue);
  }

  private mapResultToFormValue(
    mapping: PrefillMapping,
    getValue: (types: string[]) => Option<string>,
  ): Record<string, MaybeOption<unknown>> {
    return Object.entries(mapping)
      .map(
        ([fieldKey, componentTypes]) =>
          [fieldKey, getValue(componentTypes)] as const,
      )
      .reduce(
        (result, [fieldKey, value]) => {
          const otherField = this.declaration.get([
            this.fieldset.key,
            fieldKey,
          ]);
          if (otherField && isFieldDeclaration(otherField)) {
            result[fieldKey] = this._getSafeValueForField(
              otherField as CampaignField,
              value,
              result,
            );
          }

          return result;
        },
        {} as Record<string, MaybeOption<unknown>>,
      );
  }

  /**
   * Initialize Loqate Search for UK Postcodes
   * Creates Mapping which is used for UK
   */
  private initializeUKSearch() {
    const [focused$, blurred$] = partition(
      this.hasFocus$.pipe(skip(1)),
      Boolean.eqv(true),
    );

    // We split the selected search result based on its type
    const [retrieve$, detailed$] = partition(
      this.selectedSearchResult$,
      (v) => v.Type === 'Address',
    );

    // Skip emits when results are still cached within the store
    const searchValue$ = this.searchValue$.pipe(
      skipWhile(() => this.vm.hasResults()),
    );

    // Observable that emits with a detailed list of results,
    // if a non-address search result was selected
    const detailedResults$ = detailed$.pipe(
      map((container) => ({ container })),
    );

    merge(searchValue$, detailedResults$)
      .pipe(
        tap((query) => {
          this.vm.setState(AddressDataState.Loading);
        }),
        switchMap((query) => this.findAddress(query)),
        tap((r) => {
          this.vm.setResults(r);
        }),
        takeUntil(blurred$),
        repeat({ delay: () => focused$ }),
      )
      .subscribe();

    // Everytime we receive the result from a retrieve request
    // we gonna patch the form values
    retrieve$
      .pipe(
        tap(() => this.vm.setState(AddressDataState.Loading)),
        switchMap((selectedResult) => this.retrieveAddress(selectedResult.Id)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        const result = this.mapAddress(response);
        // Map values inside the address fieldset
        this._patchFormWithValues(result);
        this.closeDropdown();
      });
  }

  /**
   * Find Address
   * For UK-Postcodes the Find request has to be called 2,
   * first to search for possible Postcodes, the 2nd to get addresses for it
   *
   * @param text
   * @param response
   */
  public findAddress(query: { text: string } | { container: FindResponse }) {
    if (!this.connectionService.isOnline) {
      return EMPTY;
    }

    if ('container' in query) {
      return this.loqateService.find({
        container: query.container.Id,
        countries: this.availableCountries(),
      });
    }

    if (query.text.length === 0) {
      return of([]);
    }

    const coordinates$ = this.geolocation.latestCoordinates$.pipe(
      timeout({
        each: 500,
        with: () => of(undefined),
      }),
    );

    return combineLatest([
      coordinates$.pipe(take(1)),
      of(this._currentCountry()),
    ]).pipe(
      switchMap(([coordinates, currentCountry]) =>
        this.loqateService.find({
          text: query.text,
          countries: this.availableCountries(),
          origin: coordinates ?? currentCountry,
          bias: optionHasValue(coordinates),
        }),
      ),
    );
  }

  /**
   * Retrieve Address
   * Send the ID of the Find-Response, to retrieve the complete address
   *
   * @param id returned address of the Find-Request
   */
  public retrieveAddress(id: string) {
    return this.connectionService.isOnline
      ? this.loqateService.retrieve({ id })
      : EMPTY;
  }

  /**
   * Patches the controls form group parent with the given `values`.
   *
   * @param values Values to write to the parent form group
   * @param opts Additional options
   */
  private _patchFormWithValues(
    values: Record<string, unknown>,
    opts: {
      /** Whether the fields that are written should be marked as dirty */
      markAsDirty?: boolean;
    } = {},
  ): void {
    const form = this.control.parent as UntypedFormGroup;

    const { markAsDirty = true } = opts;
    if (markAsDirty) {
      Object.keys(values).forEach((controlKey) => {
        form.get(controlKey)?.markAsDirty();
      });
    }

    form.patchValue(values);
  }

  /**
   * Map Addresss
   * @returns the mapped fields with the correct address
   */
  private mapAddress(address: RetrieveResponse) {
    const getValue = (loqateKey: string[]): Option<string> => {
      // Find the first prop from mapping that has a value
      const responseProp = (loqateKey as (keyof RetrieveResponse)[]).find(
        (k) => !!address[k],
      );
      // If there is no value to map to, return null to clear values
      if (!responseProp) {
        return null;
      }

      const value = address[responseProp];
      return responseProp === 'CountryIso2' ? value.toLowerCase() : value;
    };

    // for each fieldkey from the mapping, get the value from the loqate response
    return this.mapResultToFormValue(this.mapping, getValue);
  }

  /**
   * Countries which should be used for the search
   * @returns array of countries
   */
  private availableCountries(): string[] {
    const country = this.declaration.findByKey('country', isFormSelectField);

    if (!country) {
      return [];
    }
    return [
      country.widgetOptions.defaultValue,
      ...country.widgetOptions.items
        .map(({ value }) => value)
        .filter((c) => c !== country?.widgetOptions.defaultValue),
    ];
  }

  /**
   * Countries which should be used for the search
   * @returns array of countries
   */
  private _currentCountry(): Maybe<string> {
    const country = this.control.parent?.get('country')?.value;

    return isString(country) ? country : undefined;
  }

  /**
   * @param field
   * @param value Value of the address search. Might be falsy
   *
   * @returns
   * The value that is safe to be used for the given `field`.
   * This comes into effect if the given `field` has value restrictions,
   * e.g. for select fields that have a predefined set of allowed items.
   * In that case, the closest matching item is used. If no value matches,
   * `undefined` is returned.
   *
   * For fields without value restrictions, the passed `value` is returned
   */
  private _getSafeValueForField(
    field: CampaignField,
    value: Option<string>,
    result: Record<string, unknown>,
  ): MaybeOption<unknown> {
    // Overwrite results of the address-search to formValues,
    // so the new values can be used to check conditions
    const updatedFormVaues = {
      ...this.form.flatValue,
      ...result,
    };

    const allowedValues = this.formHelper.getAllowedValues(
      field,
      updatedFormVaues,
    );

    // No value restrictions, return value
    if (!Array.isArray(allowedValues)) {
      return value;
    }

    // Return undefined when there is nothing to match against
    if (allowedValues.length === 0 || !isString(value)) {
      return undefined;
    }

    // Compare value against allowed values
    const weighByMatch = weighBy(stringMatching(value));
    const [first] = weighByMatch(allowedValues as string[]);
    // return the highest match with a weight above 0
    return first.weight > 0 ? first.value : undefined;
  }
}
